import {ComponentType, Dispatch, SetStateAction, useEffect, useMemo, useState} from 'react';
import moment, {Moment} from 'moment';
import {
  Box,
  Button,
  ButtonBase,
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  Stack,
  StandardTextFieldProps,
  TextField,
  TextFieldProps,
  useTheme,
} from '@mui/material';
import {KeyboardArrowLeft, KeyboardArrowRight} from '@mui/icons-material';
import {DatePicker} from '@mui/x-date-pickers';
import {Pallete} from '../../theme/bluebird';
import {useDebounce} from '../../hooks';
import {DEBOUNCE_REQUEST} from '../../utils/constants';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';

type DateStepperRange = 'day' | 'week';

type DatePickerTextFieldProps = {
  currentDate: Moment;
  range: DateStepperRange;
} & TextFieldProps;

const DatePickerTextField = ({
  currentDate,
  range,
  inputProps,
  InputProps,
  ...rest
}: DatePickerTextFieldProps) => {
  const formattedDate = useMemo(() => {
    switch (range) {
      case 'day':
        return currentDate.format('DD MMMM YYYY');
      case 'week':
        return `${currentDate.format('DD MMM')} - ${currentDate.clone().add(6, 'days').format('DD MMM')}`;
      default:
        return '';
    }
  }, [currentDate, range]);

  return (
    <ButtonBase
      sx={{
        borderRadius: '5px',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <TextField
        {...rest}
        value={formattedDate}
        inputProps={{...inputProps, readOnly: true}}
        InputProps={{...InputProps, endAdornment: null}}
      />
    </ButtonBase>
  );
};

interface DateStepperProps {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  value: Moment;
  onChange: (date: Moment) => void;
  range?: DateStepperRange;
  maxDate?: Moment;
}

export const DateStepper = ({
  setIsLoading,
  value,
  onChange,
  range = 'day',
  maxDate,
}: DateStepperProps) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const [currentDate, setCurrentDate] = useState(value);
  // Sets the current date to the right day based on the currently selected range, "snapping" the
  // given value to the start of that range. E.g., if the selected range is "week", picking any
  // day of a week will coerce the currentDate to the first day of that week.
  // *** NOTE: Use this instead of setCurrentDate directly! ***
  const setCoercedCurrentDate = (date: Moment) => {
    const coercedDate = date.clone().startOf(range === 'week' ? 'isoWeek' : 'day');
    setCurrentDate(coercedDate);
  };

  const theme = useTheme();
  const {t} = useTranslation();

  const handleNextClicked = () => {
    const date = currentDate.clone();
    date.add(1, range);
    setCoercedCurrentDate(date);
  };

  const handleBackClicked = () => {
    const date = currentDate.clone();
    date.subtract(1, range);
    setCoercedCurrentDate(date);
  };

  const handleTodayClicked = () => {
    const date = moment();
    setCoercedCurrentDate(date);
  };

  const handleDateChange = (date: Moment | null) => {
    if (date) {
      setCoercedCurrentDate(date);
    }
  };

  const onClose = () => setDatePickerOpen(false);

  useDebounce(() => onChange(currentDate), DEBOUNCE_REQUEST, [currentDate]);

  useEffect(() => setIsLoading(true), [currentDate, setIsLoading]);

  return (
    <Stack direction="row" alignItems="center" color={Pallete.white.base} spacing={1.5}>
      <Button
        onClick={handleBackClicked}
        sx={{
          paddingX: 0,
          minWidth: 0,
          '& .MuiSvgIcon-root': {
            fontSize: '28px',
          },
        }}
        color="inherit"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </Button>

      <Box onClick={() => !datePickerOpen && setDatePickerOpen(true)} sx={{}}>
        <DatePicker
          maxDate={maxDate}
          open={datePickerOpen}
          value={currentDate}
          onChange={handleDateChange}
          onClose={onClose}
          selectedSections={null}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-root': {
              padding: 0,
              cursor: 'pointer',
            },
            '& .MuiInputBase-input': {
              pointerEvents: 'none',
              width: range === 'week' ? '160px' : '190px',
              textAlign: 'center',
              fontSize: '22px',
              fontWeight: '400',
              color: Pallete.white.base,
              paddingX: 0,
              paddingY: '10px',
            },
          }}
          slotProps={{
            textField: {
              currentDate,
              range,
            } as DatePickerTextFieldProps,
          }}
          slots={{
            textField: DatePickerTextField as
              | ComponentType<
                  FilledTextFieldProps | OutlinedTextFieldProps | StandardTextFieldProps
                >
              | undefined,
          }}
        />
      </Box>

      <Button
        onClick={handleNextClicked}
        sx={{
          paddingX: 0,
          minWidth: 0,
          '& .MuiSvgIcon-root': {
            fontSize: '28px',
          },
        }}
        color="inherit"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </Button>
      <Button
        sx={{
          height: '44px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '24px',
          textTransform: 'capitalize',
        }}
        onClick={handleTodayClicked}
        variant="inverted"
      >
        {t(translations.date_time_picker.today)}
      </Button>
    </Stack>
  );
};
